/**
 * ThemeEditorDialog (Sprint 15) — manual create/edit for a single ContentTheme.
 *
 * Opened from:
 *   • Row click in ThemesTab canonical table → edit existing theme
 *   • "+ New theme" button → create new
 *
 * Manual edits bypass the review pipeline — they write directly to
 * `catalogs/theme_catalog.themes[]` via useThemeCatalog.saveThemes().
 * Only AI generations go through the history → review flow.
 */

import React, { useEffect, useState } from 'react';
import {
  Stack, Box, TextField, Alert, Typography,
} from '@mui/material';
import { BaseDialog, DialogHeader, DialogContent, DialogFooter } from '@modules/ui-components';
import type { ContentTheme } from '../types';
import { DataUsageList } from './DataUsageList';

const TESTIDS = {
  dialog: 'theme-editor-dialog',
  id: 'theme-editor-id',
  name: 'theme-editor-name',
  description: 'theme-editor-description',
  mixPct: 'theme-editor-mixpct',
  save: 'theme-editor-save',
  cancel: 'theme-editor-cancel',
} as const;

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export interface ThemeEditorDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pass existing theme to edit; omit to create */
  theme?: ContentTheme | null;
  /** Existing theme IDs (used to block duplicate ids on create). */
  existingIds?: string[];
  /** Writes the full updated theme. Parent handles merge into the array. */
  onSave: (theme: ContentTheme) => Promise<void> | void;
}

export const ThemeEditorDialog: React.FC<ThemeEditorDialogProps> = ({
  open, onClose, theme, existingIds = [], onSave,
}) => {
  const isCreate = !theme;
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mixPct, setMixPct] = useState<number>(0.1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setId(theme?.id ?? '');
    setName(theme?.name ?? '');
    setDescription((theme as unknown as { description?: string } | undefined)?.description ?? '');
    setMixPct(typeof theme?.defaultMixPct === 'number' ? theme.defaultMixPct : 0.1);
    setError(null);
  }, [open, theme]);

  const handleNameBlur = () => {
    if (isCreate && !id && name) setId(slugify(name));
  };

  const handleSave = async () => {
    setError(null);
    if (!name.trim()) { setError('Name is required'); return; }
    const finalId = (id || slugify(name)).trim();
    if (!finalId) { setError('ID could not be derived from name'); return; }
    if (isCreate && existingIds.includes(finalId)) {
      setError(`Theme with id "${finalId}" already exists`);
      return;
    }
    setSubmitting(true);
    try {
      const next: ContentTheme = {
        ...(theme ?? {}),
        id: finalId,
        name: name.trim(),
        description: description.trim() || undefined,
        defaultMixPct: mixPct,
      } as ContentTheme;
      await onSave(next);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseDialog open={open} onClose={onClose} maxWidth="sm" data-testid={TESTIDS.dialog}>
      <DialogHeader
        title={isCreate ? 'New theme' : 'Edit theme'}
        subtitle="Manual edits write directly to the canonical theme catalog"
        onClose={onClose}
      />
      <DialogContent>
        <Stack spacing={1.5} sx={{ py: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleNameBlur}
            inputProps={{ 'data-testid': TESTIDS.name }}
          />

          <TextField
            label="ID (kebab-case, derived from name if empty)"
            value={id}
            onChange={(e) => setId(slugify(e.target.value))}
            helperText={isCreate ? 'Used as a stable reference across topics/posts' : 'IDs are immutable once in use'}
            disabled={!isCreate}
            inputProps={{ 'data-testid': TESTIDS.id }}
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline minRows={2}
            helperText="~100 chars — what content belongs in this theme"
            inputProps={{ 'data-testid': TESTIDS.description }}
          />

          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Default mix ratio: {mixPct.toFixed(2)} (weight for sprint quotas)
            </Typography>
            <TextField
              type="number"
              value={mixPct}
              onChange={(e) => setMixPct(parseFloat(e.target.value) || 0)}
              inputProps={{ step: 0.05, min: 0, max: 1, 'data-testid': TESTIDS.mixPct }}
              fullWidth
              size="small"
            />
          </Box>

          {/* AI-generated audit trail — read-only in this dialog.
              Shows WHY this theme exists: which source entities drove it.
              Empty when the theme was manually created or from an older AI run. */}
          {(() => {
            const usageItems = ((theme as any)?.dataUsage?.items ?? []) as Array<{ sourcePath?: string; justification?: string }>;
            if (usageItems.length === 0) return null;
            return (
              <Box>
                <DataUsageList items={usageItems} maxItems={20} testIdPrefix="theme-editor-data-usage" />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                  Populated by AI at generation — read-only. Regenerate the theme to refresh anchors.
                </Typography>
              </Box>
            );
          })()}
        </Stack>
      </DialogContent>
      <DialogFooter
        secondaryAction={{
          label: 'Cancel', onClick: onClose, 'data-testid': TESTIDS.cancel,
        } as any}
        primaryAction={{
          label: submitting ? 'Saving…' : (isCreate ? 'Create theme' : 'Save changes'),
          onClick: handleSave,
          disabled: submitting || !name.trim(),
          'data-testid': TESTIDS.save,
        } as any}
      />
    </BaseDialog>
  );
};

export default ThemeEditorDialog;
